import {
	CustomerControllerApiFactory,
	EmissionDataRequestStatusEnum,
	ProductControllerApiFactory,
	TemplateControllerApiFactory,
} from '@api-client/api'
import { parseFiltersFromSearchString } from '@components/FilterButton'
import { parseSortingFromSearchString } from '@components/FilterButton/urlSearchString'
import { PageTitle } from '@components/PageTitle'
import { TabHeader } from '@components/TabHeader'
import {
	ProductEmissionRequestSortBy,
	QueryKey,
	ReceivedRequestSortBy,
	TemplateSortBy,
} from '@helpers/enums'
import { useAsyncApi } from '@hooks/useAsyncApi'
import { Box } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'

export type ProductsOutletContext = {
	refetchPurchasedProductsCount: () => void
	refetchSoldProductsCount: () => void
}
export interface SelectedProducts {
	id: string
	supplierId: string
	productName: string
	status?: EmissionDataRequestStatusEnum
}

const Products = () => {
	const { t } = useTranslation(['translation'])
	const productController = useAsyncApi(ProductControllerApiFactory)
	const templatesController = useAsyncApi(TemplateControllerApiFactory)
	const customerController = useAsyncApi(CustomerControllerApiFactory)

	const [purchasedProductsSearchParams, setPurchasedProductsSearchParams] =
		useState('')
	const [soldProductsSearchParams, setSoldProductsSearchParams] = useState('')
	const [receivedRequestsSearchParams, setReceivedRequestsSearchParams] =
		useState('')

	const appliedFiltersPurchased = parseFiltersFromSearchString(
		purchasedProductsSearchParams
	)
	const { sort: sortPurchased, order: orderPurchased } =
		parseSortingFromSearchString<ProductEmissionRequestSortBy>(
			purchasedProductsSearchParams
		)

	const { data, refetch: refetchPurchasedProductsCount } = useQuery({
		queryKey: [
			QueryKey.GetPurchasedProducts,
			0,
			10,
			sortPurchased,
			orderPurchased,
			appliedFiltersPurchased,
		],
		queryFn: () =>
			productController(api =>
				api.getPurchasedProducts(
					undefined,
					undefined,
					appliedFiltersPurchased.selectedSuppliers,
					appliedFiltersPurchased.lastUpdated?.from,
					appliedFiltersPurchased.lastUpdated?.to,
					undefined,
					undefined,
					appliedFiltersPurchased.productRequestStatus,
					appliedFiltersPurchased.emissionDataVerified,
					appliedFiltersPurchased.emissionValue?.from,
					appliedFiltersPurchased.emissionValue?.to,
					appliedFiltersPurchased.emissionValueConfidence,
					appliedFiltersPurchased.emissionValueGranularity,
					appliedFiltersPurchased.shareOfPrimaryDataInEmissionFactor?.from,
					appliedFiltersPurchased.shareOfPrimaryDataInEmissionFactor?.to,
					sortPurchased,
					orderPurchased,
					undefined
				)
			),
	})

	const { sort: sortSold, order: orderSold } =
		parseSortingFromSearchString<TemplateSortBy>(soldProductsSearchParams)

	const { data: dataSold, refetch: refetchSoldProductsCount } = useQuery({
		queryKey: [QueryKey.GetReceivedProductRequests, 0, 10, sortSold, orderSold],
		queryFn: () =>
			templatesController(api =>
				api.listTemplates(
					0,
					10,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					sortSold,
					orderSold,
					undefined
				)
			),
	})

	const appliedFiltersReceived = parseFiltersFromSearchString(
		receivedRequestsSearchParams
	)
	const { sort: sortReceived, order: orderReceived } =
		parseSortingFromSearchString<ReceivedRequestSortBy>(
			soldProductsSearchParams
		)

	const { data: dataReceived } = useQuery({
		queryKey: [
			QueryKey.GetSoldProducts,
			0,
			10,
			sortReceived,
			orderReceived,
			appliedFiltersReceived,
		],
		queryFn: () =>
			productController(api =>
				api.getReceivedProductRequests(
					0,
					10,
					appliedFiltersReceived.selectedCustomers,
					appliedFiltersReceived.productRequestStatus,
					sortReceived,
					orderReceived,
					appliedFiltersReceived.lastUpdated?.from,
					appliedFiltersReceived.lastUpdated?.to,
					undefined
				)
			),
	})

	// TODO (John): Confirm with design whether or not this initial
	// call is required
	useQuery({
		queryKey: [QueryKey.GetCustomers],
		queryFn: () => customerController(api => api.listCustomers(0, 500)),
	})

	return (
		<Box py={2}>
			<PageTitle text={t('translation:products')} />
			<TabHeader
				purchasedProductsSearch={purchasedProductsSearchParams}
				setPurchasedProductsSearch={setPurchasedProductsSearchParams}
				setSoldProductsSearchParams={setSoldProductsSearchParams}
				setReceivedRequestsSearchParams={setReceivedRequestsSearchParams}
				purchasedProductsCount={data?.data.metadata.count}
				soldProductsCount={dataSold?.data?.metadata.count}
				receivedProductRequestsCount={dataReceived?.data?.metadata.count}
				receivedProductRequestsSearch={receivedRequestsSearchParams}
				soldProductsSearch={soldProductsSearchParams}
			/>
			<Outlet
				context={{ refetchPurchasedProductsCount, refetchSoldProductsCount }}
			/>
		</Box>
	)
}

export { Products }
